import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {IClientOptions} from "../../models/client-options.model";
import {ClientService} from "../../services/auth/client.service";
import {MaterialModule} from "../../material.module";
import {ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {ToastService} from "../../services/layout/toast.service";
import {ClientProductionConfigSyncService} from "../../services/objects/client-production-config-sync.service";
import {environment} from "../../../environments/environment";
import {RouterLink} from '@angular/router';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {EMPTY, finalize, switchMap, take} from 'rxjs';
import {ConfirmDialogComponent} from '../confirm-dialog/confirm-dialog.component';
import {ClientPreparationService} from '../../services/objects/client-preparation.service';
import {IClientProductionConfigSyncResult} from '../../models/client-production-config-sync.model';

@Component({
  selector: 'app-client-settings',
  templateUrl: './client-settings.component.html',
  styleUrls: ['./client-settings.component.scss'],
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    RouterLink
  ],
})
export class ClientSettingsComponent implements OnInit {
    private clientService = inject(ClientService);
    private fb = inject(FormBuilder);
    private clientProductionConfigSyncService = inject(ClientProductionConfigSyncService);
    private toastService = inject(ToastService);
    private dialog = inject(MatDialog);
    private destroyRef = inject(DestroyRef);
    private changeDetector = inject(ChangeDetectorRef);
    private preparation = inject(ClientPreparationService);

  @Input() client?: IClientOptions; // Optional input
  selectedClient: IClientOptions;
  isSyncingProductionConfig = false;
  isConfirmingProductionConfig = false;
  isCheckingProductionConfig = false;
  productionConfigSyncResult: {kind: 'success' | 'warning' | 'error'; message: string} | null = null;
  isPreparingLocalData = false;
  canReloadMirroredClient = false;
  pendingProductFeeds: IClientProductionConfigSyncResult['productFeedsRequiringIndex'] = [];
  get canImportConfigurationOnly(): boolean { return this.preparation.available; }
  isProductionConfigSyncAvailable = ['local', 'development', 'beta'].includes(
      (environment.environment || '').toLowerCase()
  );

  clientForm: FormGroup;

    constructor() {
      // Initialize the client form with all fields disabled (read-only)
    this.clientForm = this.fb.group({
      id: [{ value: '', disabled: true }],
        name: [{value: '', disabled: true}],
        logo: [{value: '', disabled: true}],
        description: [{value: '', disabled: true}],
        domain: [{value: '', disabled: true}]
    });
  }

  ngOnInit(): void {
    if (this.client) {
      this.selectedClient = this.client;
    } else {
      // Initialize a new client object if creating
      this.selectedClient = this.clientService.createNewClient();
    }

    this.populateClientForm();
  }

  populateClientForm() {
    if (!this.selectedClient) return;

    // Populate the form with client data (read-only)
    this.clientForm.patchValue({
      id: this.selectedClient.id,
      name: this.selectedClient.name,
      logo: this.selectedClient.logo,
      description: this.selectedClient.description,
      domain: this.selectedClient.domain
    });
  }

  syncProductionConfig(configurationOnly = this.canImportConfigurationOnly): void {
    if (this.isSyncingProductionConfig || this.isConfirmingProductionConfig) return;
    if (configurationOnly && !this.canImportConfigurationOnly) return;

    if (!this.selectedClient?.id) {
      this.toastService.showError('No client selected.', 3000);
      return;
    }

    const clientId = this.selectedClient.id;
    this.isConfirmingProductionConfig = true;
    this.isCheckingProductionConfig = true;
    let dialogRef: MatDialogRef<ConfirmDialogComponent> | undefined;
    let revision = '';
    const prepareLocal = this.preparation.available && !configurationOnly;
    // Preview is read-only: cancelling must never prepare or change local data.
    const previewRequest = configurationOnly
      ? this.clientProductionConfigSyncService.preview(clientId, true)
      : this.clientProductionConfigSyncService.preview(clientId);
    previewRequest.pipe(
      take(1),
      switchMap(preview => {
        this.isCheckingProductionConfig = false;
        if (this.selectedClient?.id !== clientId || preview.clientId !== clientId) return EMPTY;
        revision = preview.revision;
        const replacements = preview.changes.filter(change => change.action !== 'add');
        dialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: '560px',
          data: {
            title: 'Get production config',
            message: preview.fullMirror
              ? preview.hasLocalConfiguration
                ? 'Replace ALL local configuration for this client with production? Local changes and records absent from production will be deleted, including product feeds, files and local test conversations.'
                : 'Mirror production configuration, product feeds and files for this client? Any local test conversations will be deleted.'
              : replacements.length
              ? `This import will overwrite ${replacements.length} existing local records. Local edits to these settings will be replaced by production values. Continue?`
              : 'Import production configuration for this client?',
            info: preview.fullMirror
              ? 'Only this client is affected. Production URLs remain visible; local chat testing uses localhost. Local credentials remain environment-specific. If import fails, the previous local setup is restored.'
              : 'Conversation logs and keys are not copied. Local file bindings and unchanged feed indexes are preserved. Changed feed settings require reindexing; local chatbots remain available while feeds need attention.',
            details: replacements.map(change => `${change.action === 'delete' ? 'Delete' : 'Replace'} — ${change.container}: ${change.name || change.id}`),
            confirmButtonText: preview.fullMirror ? 'Replace local client' : replacements.length ? 'Overwrite and import' : 'Import configuration',
            cancelButtonText: 'Cancel'
          }
        });
        this.changeDetector.markForCheck();
        return dialogRef.afterClosed();
      }),
      take(1),
      switchMap(confirmed => {
        this.isConfirmingProductionConfig = false;
        if (confirmed !== true) return EMPTY;
        if (this.selectedClient?.id !== clientId) {
          this.productionConfigSyncResult = {
            kind: 'error',
            message: 'The selected client changed. Start the import again for the current client.'
          };
          return EMPTY;
        }
        this.productionConfigSyncResult = null;
        this.pendingProductFeeds = [];
        this.canReloadMirroredClient = false;
        this.isSyncingProductionConfig = true;
        this.changeDetector.markForCheck();
        return configurationOnly
          ? this.clientProductionConfigSyncService.syncFromProduction(clientId, revision, false, true)
          : prepareLocal
          ? this.clientProductionConfigSyncService.syncFromProduction(clientId, revision, true)
          : this.clientProductionConfigSyncService.syncFromProduction(clientId, revision);
      }),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        dialogRef?.close();
        this.isCheckingProductionConfig = false;
        this.isPreparingLocalData = false;
        this.isConfirmingProductionConfig = false;
        this.isSyncingProductionConfig = false;
        if (!this.destroyRef.destroyed) this.changeDetector.markForCheck();
      })
    ).subscribe({
      next: (result) => {
        const upserted = result.containers.reduce((total, container) => total + container.upserted, 0);
        const preservedSecrets = result.containers.reduce((total, container) => total + container.preservedSecrets, 0);
        const deleted = result.containers.reduce((total, container) => total + (container.deleted ?? 0), 0);
        this.pendingProductFeeds = result.productFeedsRequiringIndex ?? [];
        if (result.configurationImported === false || this.pendingProductFeeds.length) {
          this.productionConfigSyncResult = {
            kind: 'warning',
            message: result.configurationImported === false
              ? 'Feed configuration imported. Process the listed feeds, then import configuration again.'
              : `Configuration imported. Chatbots and assistants are available. ${this.pendingProductFeeds.length} product feed(s) still require indexing. Unchanged local indexes were preserved.`
          };
          this.changeDetector.markForCheck();
          return;
        }
        const local = result.localPreparation;
        if (local?.clientId === clientId) this.preparation.acceptImportResult(local);
        const ready = local?.clientId === clientId && local.ready && !local.issues.length;
        this.canReloadMirroredClient = prepareLocal && ready;
        this.productionConfigSyncResult = {
          kind: prepareLocal && !ready ? 'warning' : 'success',
          message: (prepareLocal ? `Production mirrored locally. Updated ${upserted} records; deleted ${deleted} old records and test conversations.`
            : `Configuration imported. Updated ${upserted} records; preserved ${preservedSecrets} keys.`) +
            (prepareLocal ? ready ? ' Local data preparation completed. Reload Backoffice and open a fresh chat to test the new configuration.' : local?.clientId === clientId
              ? ` ${local.issues.length} local setup items still need attention. See the client status before testing.`
              : ' Local readiness was not confirmed. Check the client status before testing.' : '')
        };
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        const serverMessage = error?.error?.error;
        const message = error?.status === 0
          ? 'The connection was interrupted. Completion is unknown; check the result before importing again.'
          : typeof serverMessage === 'string' && serverMessage.trim()
            ? serverMessage
            : 'Failed to sync production config.';
        this.productionConfigSyncResult = {kind: 'error', message};
        this.changeDetector.markForCheck();
      }
    });
  }

}
